import {
  Anchor,
  Badge,
  Box,
  Card,
  Container,
  Grid,
  Group,
  Progress,
  RingProgress,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LuAnchor,
  LuChartLine,
  LuCircleCheck,
  LuCircleDollarSign,
  LuClock,
  LuFileWarning,
  LuShip,
  LuStar,
  LuTriangleAlert,
  LuUsers,
} from 'react-icons/lu'
import { Chart } from '@/frontend/components/marina/Chart'
import { CurrencyText, formatIdr } from '@/frontend/components/marina/CurrencyText'
import { DataTable } from '@/frontend/components/marina/DataTable'
import { EmptyState } from '@/frontend/components/marina/EmptyState'
import { KindBadge } from '@/frontend/components/marina/KindBadge'
import { PillNav } from '@/frontend/components/marina/PillNav'
import {
  useAnalyticsBoatDocuments,
  useAnalyticsBoats,
  useAnalyticsCustomerDocuments,
  useAnalyticsCustomers,
  useAnalyticsOverview,
  useAnalyticsRevenue,
} from '@/frontend/hooks/useAnalytics'

type SubTab = 'boats' | 'boat-documents' | 'customers' | 'customer-documents' | 'revenue'

const VALID_TABS: SubTab[] = ['boats', 'boat-documents', 'customers', 'customer-documents', 'revenue']

export const Route = createFileRoute('/dashboard/analytics')({
  validateSearch: (s: Record<string, unknown>): { tab?: SubTab } => ({
    tab: VALID_TABS.includes(s.tab as SubTab) ? (s.tab as SubTab) : undefined,
  }),
  component: AnalyticsPage,
})

// ─── Page shell ──────────────────────────────────────

function AnalyticsPage() {
  const { t } = useTranslation()
  const { tab: rawTab } = Route.useSearch()
  const sub = rawTab ?? 'boats'
  const navigate = useNavigate({ from: Route.fullPath })
  const setSub = (v: SubTab) => navigate({ search: () => ({ tab: v }), replace: true })

  const { data: overview } = useAnalyticsOverview()
  const { data: boatDocs } = useAnalyticsBoatDocuments()
  const { data: customers } = useAnalyticsCustomers()
  const { data: custDocs } = useAnalyticsCustomerDocuments()
  const { data: revenueQuick } = useAnalyticsRevenue(12)

  const missingDocsCount = boatDocs?.missingDocs?.length ?? 0
  const expiredCount = custDocs?.expiredCount ?? 0
  const totalCustomers = customers?.totalCustomers ?? 0
  const overdueCount = revenueQuick?.statusMix?.find((s) => s.status === 'OVERDUE')?.count ?? 0

  const totalBerths = (overview?.freeBerths ?? 0) + (overview?.occupiedBerths ?? 0)
  const occupancyPct = totalBerths > 0 ? Math.round(((overview?.occupiedBerths ?? 0) / totalBerths) * 100) : 0

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group gap="sm" align="flex-start" wrap="nowrap">
          <ThemeIcon variant="light" color="laut" size="xl" radius="md" style={{ flexShrink: 0 }}>
            <LuChartLine size={22} />
          </ThemeIcon>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Title order={2} fw={700} ff="var(--font-display)">
              {t('page.analytics.title')}
            </Title>
            <Text size="sm" c="dimmed">
              {t('page.analytics.subtitle')}
            </Text>
          </div>
        </Group>

        {/* Always-on summary strip */}
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
          <SummaryChip
            label="Berth Terisi"
            value={`${overview?.occupiedBerths ?? '—'} / ${totalBerths || '—'}`}
            sub={totalBerths > 0 ? `${occupancyPct}% occupancy` : undefined}
            color="laut"
            icon={<LuAnchor size={15} />}
          />
          <SummaryChip
            label="Pendapatan Bulan Ini"
            value={overview ? formatIdr(Number(overview.monthRevenueIdr)) : '—'}
            color="green"
            icon={<LuCircleDollarSign size={15} />}
            small
          />
          <SummaryChip label="Pelanggan" value={totalCustomers || '—'} color="blue" icon={<LuUsers size={15} />} />
          <SummaryChip
            label="Permintaan Pending"
            value={(overview?.pendingCount ?? 0) + (overview?.waitlistedCount ?? 0)}
            sub={
              (overview?.pendingCount ?? 0) > 0
                ? `${overview!.pendingCount} pending · ${overview!.waitlistedCount} antri`
                : 'Semua bersih'
            }
            color={(overview?.pendingCount ?? 0) > 0 ? 'karang' : 'gray'}
            icon={<LuClock size={15} />}
          />
        </SimpleGrid>

        {/* Pill navigation */}
        <PillNav
          value={sub}
          onChange={setSub}
          items={[
            { value: 'boats', label: t('page.analytics.tab_fleet'), icon: <LuShip size={13} /> },
            {
              value: 'boat-documents',
              label: t('page.analytics.tab_boat_docs'),
              icon: <LuFileWarning size={13} />,
              count: missingDocsCount,
              countColor: 'karang',
            },
            {
              value: 'customers',
              label: t('page.analytics.tab_customers'),
              icon: <LuUsers size={13} />,
              count: totalCustomers,
              countColor: 'gray',
            },
            {
              value: 'customer-documents',
              label: t('page.analytics.tab_id_docs'),
              icon: <LuFileWarning size={13} />,
              count: expiredCount,
              countColor: 'red',
            },
            {
              value: 'revenue',
              label: t('page.analytics.tab_revenue'),
              icon: <LuCircleDollarSign size={13} />,
              count: overdueCount,
              countColor: 'red',
            },
          ]}
        />

        <Box>
          {sub === 'boats' && <BoatsAnalytics />}
          {sub === 'boat-documents' && <BoatDocumentsAnalytics />}
          {sub === 'customers' && <CustomersAnalyticsPanel />}
          {sub === 'customer-documents' && <CustomerDocumentsAnalytics />}
          {sub === 'revenue' && <RevenueAnalytics />}
        </Box>
      </Stack>
    </Container>
  )
}

// ─── Boats ───────────────────────────────────────────

function BoatsAnalytics() {
  const { data, isLoading } = useAnalyticsBoats()

  if (isLoading) return <SectionSkeleton kpis={4} blocks={2} />
  if (!data)
    return (
      <EmptyState
        kind="dock"
        title="Belum ada data armada"
        description="Aktivitas pertama akan memunculkan statistik di sini."
      />
    )

  const totalFleet = data.currentlyDocked + data.boatsDepartedToday

  return (
    <Stack gap="xl">
      <Stack gap="sm">
        <SectionHead title="Aktivitas Hari Ini" />
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
          <KpiCard value={data.boatsArrivedToday} label="Tiba Hari Ini" icon={<LuShip size={18} />} color="laut" />
          <KpiCard
            value={data.boatsDepartedToday}
            label="Berangkat Hari Ini"
            icon={<LuShip size={18} />}
            color="karang"
          />
          <KpiCard
            value={data.currentlyDocked}
            label="Sedang Berlabuh"
            icon={<LuAnchor size={18} />}
            color="blue"
            sub={totalFleet > 0 ? `${Math.round((data.currentlyDocked / totalFleet) * 100)}% aktif` : undefined}
          />
          <KpiCard
            value={data.frequentVisitingBoats.length}
            label="Pelanggan Setia"
            icon={<LuStar size={18} />}
            color="yellow"
            sub="≥ 2 kunjungan"
          />
        </SimpleGrid>
      </Stack>

      {data.delayedArrivals.length > 0 && (
        <Box
          p="md"
          style={{
            borderRadius: 12,
            background: 'light-dark(var(--mantine-color-karang-0), rgba(180,80,60,0.12))',
          }}
        >
          <Group gap="xs" mb="sm">
            <ThemeIcon variant="light" color="red" size="sm" radius="xl">
              <LuTriangleAlert size={13} />
            </ThemeIcon>
            <Text size="xs" fw={700} c="red.7" tt="uppercase" style={{ letterSpacing: '0.06em' }}>
              {data.delayedArrivals.length} keterlambatan
            </Text>
          </Group>
          <Stack gap="xs">
            {data.delayedArrivals.map((r) => (
              <Group key={r.id} justify="space-between" wrap="nowrap" gap="sm">
                <div style={{ minWidth: 0 }}>
                  <Text size="sm" fw={600} truncate>
                    {r.boatName}
                  </Text>
                  <Text size="xs" c="dimmed" truncate>
                    {r.customerName} · {dayjs(r.arrivalDate).format('DD MMM, HH:mm')}
                  </Text>
                </div>
                <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                  <Badge color={r.daysDelayed > 7 ? 'red' : 'orange'} variant="light" size="sm">
                    +{r.daysDelayed}h
                  </Badge>
                  <Anchor component={Link} to={`/dashboard/reservations/${r.id}` as any} size="xs" c="laut">
                    →
                  </Anchor>
                </Group>
              </Group>
            ))}
          </Stack>
        </Box>
      )}

      <Stack gap="sm">
        <SectionHead title="Komposisi Armada" />
        <Grid>
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Card radius="md" padding="lg" h="100%">
              <CardLabel>Tipe Kapal</CardLabel>
              {data.byType.length === 0 ? (
                <EmptyBlock label="Belum ada kapal terdaftar" />
              ) : (
                <Chart option={buildByTypeChart(data.byType)} height={260} />
              )}
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Card radius="md" padding="lg" h="100%">
              <CardLabel>Distribusi Ukuran</CardLabel>
              {data.sizeDistribution.total === 0 ? (
                <EmptyBlock label="Belum ada data" />
              ) : (
                <Chart option={buildSizeDonut(data.sizeDistribution)} height={260} />
              )}
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>

      <Stack gap="sm">
        <SectionHead title="Keterlibatan Pelanggan" />
        <Grid>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Card radius="md" padding="lg" h="100%">
              <CardLabel>Kapal Sering Berkunjung</CardLabel>
              <DataTable
                isEmpty={data.frequentVisitingBoats.length === 0}
                emptyLabel="Belum ada pengunjung berulang"
                columns={
                  <>
                    <Table.Th>Kapal</Table.Th>
                    <Table.Th>Pelanggan</Table.Th>
                    <Table.Th ta="right">Kunjungan</Table.Th>
                    <Table.Th>Terakhir</Table.Th>
                  </>
                }
              >
                {data.frequentVisitingBoats.map((r, i) => (
                  <Table.Tr key={r.boatId}>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        {i < 3 && (
                          <Text size="xs" style={{ opacity: 0.6 }}>
                            {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}
                          </Text>
                        )}
                        <Anchor component={Link} to={`/dashboard/accounts/boats/${r.boatId}` as any} fw={500} size="sm">
                          {r.name}
                        </Anchor>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{r.customerName}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Badge variant="light" color="laut">
                        {r.visitCount}×
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {dayjs(r.lastVisit).format('DD MMM YYYY')}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </DataTable>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card radius="md" padding="lg" h="100%">
              <Group justify="space-between" mb="md" align="center">
                <CardLabel noMargin>Tanpa Kapal</CardLabel>
                {data.customersWithoutBoats.length > 0 && (
                  <Badge variant="light" color="orange" size="xs">
                    {data.customersWithoutBoats.length}
                  </Badge>
                )}
              </Group>
              {data.customersWithoutBoats.length === 0 ? (
                <EmptyBlock label="Semua pelanggan punya kapal 🎯" />
              ) : (
                <Stack gap="xs">
                  {data.customersWithoutBoats.slice(0, 6).map((c) => (
                    <Group key={c.id} justify="space-between" wrap="nowrap" gap="sm">
                      <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
                        <Text size="sm" fw={500} truncate>
                          {c.firstName} {c.lastName}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>
                          {c.email ?? '—'}
                        </Text>
                      </Stack>
                      <Anchor component={Link} to={`/dashboard/accounts/customers/${c.id}` as any} c="laut" size="xs">
                        + Kapal
                      </Anchor>
                    </Group>
                  ))}
                </Stack>
              )}
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>
    </Stack>
  )
}

// ─── Boat Documents ──────────────────────────────────

const DOC_FIELDS = [
  { key: 'registration', label: 'Registrasi', short: 'REG' },
  { key: 'imoNumber', label: 'Nomor IMO', short: 'IMO' },
  { key: 'hullId', label: 'Hull ID', short: 'HULL' },
  { key: 'flagState', label: 'Bendera', short: 'FLAG' },
] as const

type DocFieldKey = (typeof DOC_FIELDS)[number]['key']

function BoatDocumentsAnalytics() {
  const { data, isLoading } = useAnalyticsBoatDocuments()
  const [filterField, setFilterField] = useState<DocFieldKey | 'all'>('all')

  if (isLoading) return <SectionSkeleton kpis={4} blocks={2} />
  if (!data)
    return (
      <EmptyState
        kind="sextant"
        title="Belum ada data dokumen"
        description="Statistik dokumen kapal akan muncul di sini."
      />
    )

  const pct = (n: number) => (data.total === 0 ? 0 : Math.round((n / data.total) * 100))

  const fieldStats = [
    {
      ...DOC_FIELDS[0],
      count: data.withRegistration,
      p: pct(data.withRegistration),
      missing: data.total - data.withRegistration,
    },
    { ...DOC_FIELDS[1], count: data.withImo, p: pct(data.withImo), missing: data.total - data.withImo },
    { ...DOC_FIELDS[2], count: data.withHullId, p: pct(data.withHullId), missing: data.total - data.withHullId },
    {
      ...DOC_FIELDS[3],
      count: data.withFlagState,
      p: pct(data.withFlagState),
      missing: data.total - data.withFlagState,
    },
  ]

  // fix: include all 4 fields in overall calc
  const overallPct =
    data.total === 0
      ? 0
      : Math.round(
          ((data.withRegistration + data.withImo + data.withHullId + data.withFlagState) / (data.total * 4)) * 100,
        )

  const totalMissingItems = fieldStats.reduce((s, f) => s + f.missing, 0)
  const boatsWithIssues = data.missingDocs.length
  const ringColor = overallPct >= 80 ? 'teal' : overallPct >= 50 ? 'yellow' : 'red'

  // filter missing docs by selected field
  const filteredDocs =
    filterField === 'all' ? data.missingDocs : data.missingDocs.filter((b) => b[filterField] === null)

  return (
    <Stack gap="xl">
      {/* KPI strip */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
        <KpiCard value={data.total} label="Total Kapal" icon={<LuShip size={18} />} color="blue" />
        <KpiCard
          value={boatsWithIssues}
          label="Kapal Bermasalah"
          icon={<LuFileWarning size={18} />}
          color={boatsWithIssues > 0 ? 'orange' : 'teal'}
          sub={boatsWithIssues > 0 ? `${data.total - boatsWithIssues} sudah lengkap` : 'Semua lengkap 🎉'}
        />
        <KpiCard
          value={totalMissingItems}
          label="Total Item Hilang"
          icon={<LuTriangleAlert size={18} />}
          color={totalMissingItems > 0 ? 'red' : 'teal'}
          sub={`dari ${data.total * 4} item wajib`}
        />
        <KpiCard
          value={`${overallPct}%`}
          label="Tingkat Kepatuhan"
          icon={<LuCircleCheck size={18} />}
          color={ringColor}
          sub={overallPct >= 80 ? 'Baik' : overallPct >= 50 ? 'Perlu perhatian' : 'Kritis'}
          small
        />
      </SimpleGrid>

      {/* Completeness overview */}
      <Stack gap="sm">
        <SectionHead title="Kelengkapan per Tipe Dokumen" />
        <Grid>
          {/* Ring + health */}
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <Card radius="md" padding="lg" h="100%">
              <Stack align="center" justify="center" gap="sm" h="100%" py="xs">
                <RingProgress
                  size={150}
                  thickness={14}
                  roundCaps
                  sections={[{ value: overallPct, color: ringColor }]}
                  label={
                    <Stack gap={1} align="center">
                      <Text fw={800} size="xl" lh={1} c={ringColor}>
                        {overallPct}%
                      </Text>
                      <Text size="xs" c="dimmed" lh={1.2} ta="center">
                        kepatuhan
                        <br />
                        dokumen
                      </Text>
                    </Stack>
                  }
                />
                <Box ta="center">
                  <Badge size="sm" color={ringColor} variant="light" radius="xl">
                    {overallPct >= 80 ? '✓ Baik' : overallPct >= 50 ? '⚠ Perlu perhatian' : '✕ Kritis'}
                  </Badge>
                  <Text size="xs" c="dimmed" mt={6}>
                    {data.total} kapal · {data.total * 4} item wajib
                  </Text>
                </Box>
              </Stack>
            </Card>
          </Grid.Col>

          {/* 4 doc field cards */}
          <Grid.Col span={{ base: 12, sm: 9 }}>
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm" h="100%">
              {fieldStats.map((f) => {
                const c = f.p >= 80 ? 'teal' : f.p >= 50 ? 'yellow' : 'red'
                const isActive = filterField === f.key
                return (
                  <Card
                    key={f.key}
                    radius="md"
                    padding="md"
                    style={{
                      cursor: 'pointer',
                      outline: isActive ? `2px solid var(--mantine-color-laut-6)` : 'none',
                      outlineOffset: 2,
                      transition: 'outline 120ms ease',
                    }}
                    onClick={() => setFilterField(isActive ? 'all' : f.key)}
                  >
                    <Stack gap="xs">
                      <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
                          {f.label}
                        </Text>
                        <Badge size="xs" color={c} variant="light" style={{ flexShrink: 0 }}>
                          {f.p}%
                        </Badge>
                      </Group>
                      <Group gap={4} align="baseline">
                        <Text fw={700} size="xl" lh={1} c={c}>
                          {f.count}
                        </Text>
                        <Text size="xs" c="dimmed">
                          / {data.total}
                        </Text>
                      </Group>
                      <Progress value={f.p} color={c} size="xs" radius="xl" />
                      {f.missing > 0 && (
                        <Text size="xs" c="red.6" fw={500}>
                          {f.missing} belum diisi
                        </Text>
                      )}
                    </Stack>
                  </Card>
                )
              })}
            </SimpleGrid>
          </Grid.Col>
        </Grid>
      </Stack>

      {/* Missing docs table */}
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <SectionHead
            title={
              filterField === 'all'
                ? 'Kapal dengan Dokumen Tidak Lengkap'
                : `Filter: ${DOC_FIELDS.find((f) => f.key === filterField)?.label}`
            }
            badge={filteredDocs.length > 0 ? { count: filteredDocs.length, color: 'orange' } : undefined}
            noMargin
          />
          {filterField !== 'all' && (
            <UnstyledButton
              onClick={() => setFilterField('all')}
              style={{ fontSize: '0.8rem', color: 'var(--mantine-color-laut-6)' }}
            >
              ✕ Reset filter
            </UnstyledButton>
          )}
        </Group>

        <Card radius="md" padding="lg">
          <DataTable
            isEmpty={filteredDocs.length === 0}
            emptyLabel={
              filterField === 'all'
                ? 'Semua kapal dokumennya lengkap 🎉'
                : 'Tidak ada kapal yang melewatkan dokumen ini'
            }
            columns={
              <>
                <Table.Th>Kapal</Table.Th>
                <Table.Th>Pelanggan</Table.Th>
                <Table.Th>Dokumen Hilang</Table.Th>
                <Table.Th ta="center">Kelengkapan</Table.Th>
                <Table.Th ta="right">Aksi</Table.Th>
              </>
            }
          >
            {filteredDocs.map((b) => {
              const missingList = DOC_FIELDS.filter((f) => b[f.key] === null)
              const presentCount = 4 - missingList.length
              const severity = missingList.length >= 3 ? 'red' : missingList.length >= 2 ? 'orange' : 'yellow'
              return (
                <Table.Tr key={b.id}>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {b.name}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {b.customerName}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="wrap">
                      {missingList.map((f) => (
                        <Tooltip key={f.key} label={f.label} withArrow position="top">
                          <Badge size="xs" color="red" variant="light">
                            {f.short}
                          </Badge>
                        </Tooltip>
                      ))}
                    </Group>
                  </Table.Td>
                  <Table.Td ta="center">
                    <Group gap={6} justify="center" wrap="nowrap">
                      <Text size="xs" fw={700} c={severity}>
                        {presentCount}/4
                      </Text>
                      <Progress value={(presentCount / 4) * 100} color={severity} size="sm" radius="xl" w={48} />
                    </Group>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Anchor
                      component={Link}
                      to={`/dashboard/accounts/boats/${b.id}` as any}
                      c="laut"
                      size="sm"
                      fw={500}
                    >
                      Lengkapi →
                    </Anchor>
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </DataTable>
        </Card>
      </Stack>
    </Stack>
  )
}

// ─── Customers ───────────────────────────────────────

function CustomersAnalyticsPanel() {
  const { data, isLoading } = useAnalyticsCustomers()

  if (isLoading) return <SectionSkeleton kpis={4} blocks={2} />
  if (!data)
    return (
      <EmptyState
        kind="compass"
        title="Belum ada data pelanggan"
        description="Statistik pelanggan akan muncul setelah pendaftaran pertama."
      />
    )

  const totalRevenue = data.topByRevenue.reduce((s, c) => s + Number(c.total), 0)
  const avgRevenue = data.topByRevenue.length > 0 ? totalRevenue / data.topByRevenue.length : 0
  const topCat = [...data.byCategory].sort((a, b) => b.count - a.count)[0]
  const maxCatCount = topCat?.count ?? 1
  const maxRevenue = Number(data.topByRevenue[0]?.total ?? 1)
  const top3 = data.topByRevenue.slice(0, 3)
  const rest = data.topByRevenue.slice(3)

  const podiumOrder = top3.length === 3 ? [top3[1], top3[0], top3[2]] : top3
  const podiumMeta = [
    { medal: '🥈', color: 'gray', height: 80 },
    { medal: '🥇', color: 'yellow', height: 110 },
    { medal: '🥉', color: 'orange', height: 64 },
  ]

  return (
    <Stack gap="xl">
      {/* KPI strip */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
        <KpiCard value={data.totalCustomers} label="Total Pelanggan" icon={<LuUsers size={18} />} color="blue" />
        <KpiCard
          value={data.topByRevenue.length}
          label="Punya Revenue"
          icon={<LuStar size={18} />}
          color="laut"
          sub={data.topByRevenue.length > 0 ? `Avg ${formatIdr(avgRevenue)}` : undefined}
          small
        />
        <KpiCard
          value={formatIdr(totalRevenue)}
          label="Total Revenue Terbayar"
          icon={<LuCircleDollarSign size={18} />}
          color="green"
          small
        />
        <KpiCard
          value={topCat?.category ?? '—'}
          label="Kategori Terbanyak"
          icon={<LuUsers size={18} />}
          color="violet"
          sub={topCat ? `${topCat.count} pelanggan` : undefined}
          small
        />
      </SimpleGrid>

      {/* Category distribution — tile grid */}
      <Stack gap="sm">
        <SectionHead title="Distribusi Kategori" />
        {data.byCategory.length === 0 ? (
          <EmptyBlock label="Belum ada data kategori" />
        ) : (
          <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} spacing="sm">
            {[...data.byCategory]
              .sort((a, b) => b.count - a.count)
              .map((c) => {
                const share = Math.round((c.count / data.totalCustomers) * 100)
                return (
                  <Card key={c.category} radius="md" padding="sm">
                    <Stack gap={6}>
                      <KindBadge category={c.category} />
                      <Group gap={4} align="baseline">
                        <Text fw={700} size="lg" lh={1}>
                          {c.count}
                        </Text>
                        <Text size="xs" c="dimmed">
                          ({share}%)
                        </Text>
                      </Group>
                      <Progress value={(c.count / maxCatCount) * 100} size="xs" radius="xl" color="laut" />
                    </Stack>
                  </Card>
                )
              })}
          </SimpleGrid>
        )}
      </Stack>

      {/* Revenue leaderboard */}
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <SectionHead title="Revenue Leaders" noMargin />
          <Anchor component={Link} to="/dashboard/accounts/customers" c="laut" size="xs">
            Semua pelanggan →
          </Anchor>
        </Group>

        {data.topByRevenue.length === 0 ? (
          <Card radius="md" padding="xl">
            <EmptyBlock label="Belum ada invoice terbayar" />
          </Card>
        ) : (
          <Stack gap="md">
            {/* Podium top-3 */}
            {top3.length > 0 && (
              <SimpleGrid cols={{ base: 1, sm: top3.length }} spacing="sm">
                {(top3.length === 3 ? podiumOrder : top3).map((c, idx) => {
                  const realIdx = top3.length === 3 ? [1, 0, 2][idx] : idx
                  const meta = podiumMeta[idx] ?? { medal: `#${realIdx + 1}`, color: 'gray', height: 60 }
                  const share = totalRevenue > 0 ? (Number(c.total) / totalRevenue) * 100 : 0
                  const barWidth = (Number(c.total) / maxRevenue) * 100
                  return (
                    <Card
                      key={c.customerId}
                      radius="md"
                      padding="md"
                      style={{
                        background:
                          realIdx === 0 ? 'light-dark(rgba(250,210,80,0.08), rgba(250,210,80,0.06))' : undefined,
                      }}
                    >
                      <Stack gap="xs">
                        <Group justify="space-between" align="flex-start">
                          <Text size="xl">{meta.medal}</Text>
                          <Badge size="xs" color={meta.color} variant="light">
                            #{realIdx + 1}
                          </Badge>
                        </Group>
                        <div>
                          <Text fw={700} size="sm" truncate>
                            {c.name}
                          </Text>
                          <CurrencyText idr={c.total} fw={800} size="md" c="navy.8" />
                        </div>
                        <div>
                          <Text size="xs" c="dimmed" mb={4}>
                            {share.toFixed(1)}% dari total revenue
                          </Text>
                          <Progress value={barWidth} color={meta.color} size="sm" radius="xl" />
                        </div>
                        <Anchor
                          component={Link}
                          to={`/dashboard/accounts/customers/${c.customerId}` as any}
                          size="xs"
                          c="laut"
                        >
                          Lihat profil →
                        </Anchor>
                      </Stack>
                    </Card>
                  )
                })}
              </SimpleGrid>
            )}

            {/* Rest #4+ compact table */}
            {rest.length > 0 && (
              <Card radius="md" padding="lg">
                <CardLabel>Peringkat Lainnya</CardLabel>
                <DataTable
                  isEmpty={false}
                  columns={
                    <>
                      <Table.Th w={50}>Rank</Table.Th>
                      <Table.Th>Pelanggan</Table.Th>
                      <Table.Th ta="right">Revenue</Table.Th>
                      <Table.Th ta="right">Share</Table.Th>
                      <Table.Th ta="right" />
                    </>
                  }
                >
                  {rest.map((c, i) => {
                    const share = totalRevenue > 0 ? ((Number(c.total) / totalRevenue) * 100).toFixed(1) : '0'
                    const barWidth = (Number(c.total) / maxRevenue) * 100
                    return (
                      <Table.Tr key={c.customerId}>
                        <Table.Td>
                          <Text size="sm" c="dimmed" ta="center">
                            #{i + 4}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {c.name}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <CurrencyText idr={c.total} fw={600} size="sm" />
                        </Table.Td>
                        <Table.Td ta="right">
                          <Group gap={6} justify="flex-end" wrap="nowrap">
                            <Text size="xs" c="dimmed">
                              {share}%
                            </Text>
                            <Progress value={barWidth} color="laut" size="xs" radius="xl" w={40} />
                          </Group>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Anchor
                            component={Link}
                            to={`/dashboard/accounts/customers/${c.customerId}` as any}
                            c="laut"
                            size="sm"
                          >
                            →
                          </Anchor>
                        </Table.Td>
                      </Table.Tr>
                    )
                  })}
                </DataTable>
              </Card>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

// ─── Customer Documents ──────────────────────────────

type DocFilter = 'all' | 'expired' | 'soon'

function CustomerDocumentsAnalytics() {
  const { data, isLoading } = useAnalyticsCustomerDocuments()
  const [filter, setFilter] = useState<DocFilter>('all')

  if (isLoading) return <SectionSkeleton kpis={4} blocks={2} />
  if (!data)
    return (
      <EmptyState
        kind="sextant"
        title="Belum ada data dokumen pelanggan"
        description="Status ID dan masa berlaku akan dimonitor di sini."
      />
    )

  const pct = (n: number) => (data.total === 0 ? 0 : Math.round((n / data.total) * 100))
  const idPct = pct(data.withIdNumber)
  const taxPct = pct(data.withTaxId)

  // unified + sorted: expired first (most recently expired = smallest daysLeft), then expiring soon (soonest first)
  type DocRow = (typeof data.expired)[number] & { _kind: 'expired' | 'soon' }
  const allRows: DocRow[] = [
    ...data.expired.map((r) => ({ ...r, _kind: 'expired' as const })),
    ...data.expiringSoon.map((r) => ({ ...r, _kind: 'soon' as const })),
  ].sort((a, b) => {
    const da = a.idExpiry ? dayjs(a.idExpiry).diff(dayjs(), 'day') : 9999
    const db = b.idExpiry ? dayjs(b.idExpiry).diff(dayjs(), 'day') : 9999
    return da - db
  })

  const filteredRows =
    filter === 'expired'
      ? allRows.filter((r) => r._kind === 'expired')
      : filter === 'soon'
        ? allRows.filter((r) => r._kind === 'soon')
        : allRows

  return (
    <Stack gap="xl">
      {/* KPI strip */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
        <KpiCard value={data.total} label="Total Pelanggan" icon={<LuUsers size={18} />} color="blue" />
        <KpiCard
          value={`${idPct}%`}
          label="Punya Nomor ID"
          icon={<LuCircleCheck size={18} />}
          color={idPct >= 80 ? 'teal' : 'yellow'}
          sub={`${data.withIdNumber} dari ${data.total}`}
          small
        />
        <KpiCard
          value={`${taxPct}%`}
          label="Punya NPWP"
          icon={<LuCircleCheck size={18} />}
          color={taxPct >= 80 ? 'teal' : 'yellow'}
          sub={`${data.withTaxId} dari ${data.total}`}
          small
        />
        <KpiCard
          value={data.expiredCount + data.expiringSoonCount}
          label="Perlu Diperbarui"
          icon={<LuFileWarning size={18} />}
          color={data.expiredCount > 0 ? 'red' : data.expiringSoonCount > 0 ? 'orange' : 'gray'}
          sub={
            data.expiredCount > 0
              ? `${data.expiredCount} kedaluwarsa · ${data.expiringSoonCount} segera`
              : data.expiringSoonCount > 0
                ? `${data.expiringSoonCount} dalam 90 hari`
                : 'Semua aktif'
          }
        />
      </SimpleGrid>

      {/* Completeness overview */}
      <Stack gap="sm">
        <SectionHead title="Kelengkapan Dokumen ID" />
        <Grid>
          {[
            {
              label: 'Nomor KTP / Paspor',
              count: data.withIdNumber,
              missing: data.total - data.withIdNumber,
              p: idPct,
            },
            { label: 'NPWP / Tax ID', count: data.withTaxId, missing: data.total - data.withTaxId, p: taxPct },
          ].map((f) => {
            const c = f.p >= 80 ? 'teal' : f.p >= 50 ? 'yellow' : 'red'
            return (
              <Grid.Col key={f.label} span={{ base: 12, sm: 6 }}>
                <Card radius="md" padding="md">
                  <Group justify="space-between" align="flex-start" mb="sm">
                    <div>
                      <Text size="sm" fw={600}>
                        {f.label}
                      </Text>
                      <Text size="xs" c="dimmed" mt={2}>
                        {f.count} dari {data.total} pelanggan
                      </Text>
                    </div>
                    <Group gap="xs" align="center">
                      <RingProgress
                        size={52}
                        thickness={5}
                        roundCaps
                        sections={[{ value: f.p, color: c }]}
                        label={
                          <Text size="xs" fw={700} ta="center" c={c}>
                            {f.p}%
                          </Text>
                        }
                      />
                    </Group>
                  </Group>
                  <Progress value={f.p} color={c} size="sm" radius="xl" />
                  {f.missing > 0 && (
                    <Text size="xs" c="red.6" fw={500} mt={6}>
                      {f.missing} pelanggan belum mengisi
                    </Text>
                  )}
                </Card>
              </Grid.Col>
            )
          })}
        </Grid>
      </Stack>

      {/* Alert banners */}
      {(data.expiredCount > 0 || data.expiringSoonCount > 0) && (
        <Stack gap="xs">
          {data.expiredCount > 0 && (
            <Box
              px="md"
              py="sm"
              style={{
                borderRadius: 10,
                background: 'light-dark(var(--mantine-color-red-0), rgba(200,60,60,0.1))',
              }}
            >
              <Group gap="xs">
                <ThemeIcon variant="light" color="red" size="sm" radius="xl">
                  <LuTriangleAlert size={13} />
                </ThemeIcon>
                <Text size="sm" fw={600} c="red.7">
                  {data.expiredCount} ID sudah kedaluwarsa — segera hubungi pelanggan
                </Text>
              </Group>
            </Box>
          )}
          {data.expiringSoonCount > 0 && (
            <Box
              px="md"
              py="sm"
              style={{
                borderRadius: 10,
                background: 'light-dark(var(--mantine-color-orange-0), rgba(200,120,40,0.1))',
              }}
            >
              <Group gap="xs">
                <ThemeIcon variant="light" color="orange" size="sm" radius="xl">
                  <LuFileWarning size={13} />
                </ThemeIcon>
                <Text size="sm" fw={600} c="orange.7">
                  {data.expiringSoonCount} ID akan kedaluwarsa dalam 90 hari
                </Text>
              </Group>
            </Box>
          )}
        </Stack>
      )}

      {/* Unified table with filter pills */}
      {allRows.length > 0 && (
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <SectionHead title="Daftar Perlu Tindakan" noMargin />
            <Group gap="xs">
              {(
                [
                  { value: 'all', label: `Semua (${allRows.length})` },
                  { value: 'expired', label: `Kedaluwarsa (${data.expiredCount})`, color: 'red' },
                  { value: 'soon', label: `Segera (${data.expiringSoonCount})`, color: 'orange' },
                ] as { value: DocFilter; label: string; color?: string }[]
              ).map((opt) => (
                <UnstyledButton
                  key={opt.value}
                  onClick={() => setFilter(opt.value)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: 999,
                    fontSize: '0.78rem',
                    fontWeight: filter === opt.value ? 600 : 500,
                    background:
                      filter === opt.value
                        ? opt.color
                          ? `var(--mantine-color-${opt.color}-6)`
                          : 'var(--mantine-color-laut-6)'
                        : 'transparent',
                    color: filter === opt.value ? 'white' : 'var(--mantine-color-dimmed)',
                    transition: 'background 120ms ease',
                  }}
                >
                  {opt.label}
                </UnstyledButton>
              ))}
            </Group>
          </Group>

          <Card radius="md" padding="lg">
            <DataTable
              isEmpty={filteredRows.length === 0}
              emptyLabel="Tidak ada data untuk filter ini"
              columns={
                <>
                  <Table.Th>Pelanggan</Table.Th>
                  <Table.Th>No. ID</Table.Th>
                  <Table.Th>Kedaluwarsa</Table.Th>
                  <Table.Th ta="center">Status</Table.Th>
                  <Table.Th ta="right">Aksi</Table.Th>
                </>
              }
            >
              {filteredRows.map((c) => {
                const daysLeft = c.idExpiry ? dayjs(c.idExpiry).diff(dayjs(), 'day') : null
                const isExpired = c._kind === 'expired'
                const urgency = isExpired ? 'red' : daysLeft !== null && daysLeft < 30 ? 'orange' : 'yellow'
                return (
                  <Table.Tr key={`${c.id}-${c._kind}`}>
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {c.firstName} {c.lastName}
                      </Text>
                      <Text size="xs" c="dimmed" truncate>
                        {c.email ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text
                        size="sm"
                        ff="var(--mantine-font-family-monospace, monospace)"
                        c={c.idNumber ? undefined : 'dimmed'}
                      >
                        {c.idNumber ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {c.idExpiry ? (
                        <Text size="sm">{dayjs(c.idExpiry).format('DD MMM YYYY')}</Text>
                      ) : (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td ta="center">
                      {daysLeft !== null ? (
                        <Badge color={urgency} variant={isExpired ? 'filled' : 'light'} size="sm" radius="xl">
                          {isExpired
                            ? `${Math.abs(daysLeft)}h lalu`
                            : daysLeft === 0
                              ? 'Hari ini'
                              : `${daysLeft}h lagi`}
                        </Badge>
                      ) : (
                        <Text size="xs" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      <Anchor
                        component={Link}
                        to={`/dashboard/accounts/customers/${c.id}` as any}
                        c="laut"
                        size="sm"
                        fw={500}
                      >
                        Perbarui →
                      </Anchor>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </DataTable>
          </Card>
        </Stack>
      )}

      {allRows.length === 0 && (
        <Card radius="md" padding="xl">
          <Stack align="center" gap="xs">
            <Text size="xl">🎉</Text>
            <Text fw={600}>Semua dokumen ID aktif dan valid</Text>
            <Text size="sm" c="dimmed">
              Tidak ada ID yang kedaluwarsa atau akan segera kadaluwarsa.
            </Text>
          </Stack>
        </Card>
      )}
    </Stack>
  )
}

// ─── Revenue ─────────────────────────────────────────

function RevenueAnalytics() {
  const [period, setPeriod] = useState<'3' | '6' | '12' | '24'>('12')
  const months = Number(period)
  const { data, isLoading } = useAnalyticsRevenue(months)

  if (isLoading) return <SectionSkeleton kpis={5} blocks={2} />
  if (!data)
    return (
      <EmptyState
        kind="buoy"
        title="Belum ada data pendapatan"
        description="Rekap dan tren revenue akan muncul setelah ada transaksi."
      />
    )

  const buckets = data.buckets
  const lastMonth = buckets[buckets.length - 1]?.revenue ?? 0
  const prevMonth = buckets[buckets.length - 2]?.revenue ?? 0
  const momDelta = prevMonth > 0 ? Math.round(((lastMonth - prevMonth) / prevMonth) * 100) : null
  const periodTotal = buckets.reduce((s, b) => s + b.revenue, 0)
  const peakBucket = [...buckets].sort((a, b) => b.revenue - a.revenue)[0]
  const avgMonthly = buckets.length > 0 ? periodTotal / buckets.length : 0

  const paid = data.statusMix.find((s) => s.status === 'PAID')
  const overdue = data.statusMix.find((s) => s.status === 'OVERDUE')
  const sent = data.statusMix.find((s) => s.status === 'SENT')
  const partiallyPaid = data.statusMix.find((s) => s.status === 'PARTIALLY_PAID')

  const paidTotal = Number(paid?.total ?? 0)
  const partialTotal = Number(partiallyPaid?.total ?? 0)
  const sentTotal = Number(sent?.total ?? 0)
  const overdueTotal = Number(overdue?.total ?? 0)
  const outstanding = sentTotal + partialTotal
  const outstandingCount = (sent?.count ?? 0) + (partiallyPaid?.count ?? 0)
  const collectable = paidTotal + outstanding + overdueTotal
  const collectionRate = collectable > 0 ? Math.round((paidTotal / collectable) * 100) : 0

  // cashflow proportion: paid | outstanding | overdue | other
  const otherTotal = data.statusMix
    .filter((s) => !['PAID', 'SENT', 'PARTIALLY_PAID', 'OVERDUE'].includes(s.status))
    .reduce((s, m) => s + Number(m.total), 0)
  const cashTotal = paidTotal + outstanding + overdueTotal + otherTotal

  // grouped status mix for display
  const priorityOrder = ['PAID', 'PARTIALLY_PAID', 'SENT', 'OVERDUE', 'DRAFT', 'PRO_FORMA', 'CANCELLED']
  const sortedMix = [...data.statusMix].sort(
    (a, b) => priorityOrder.indexOf(a.status) - priorityOrder.indexOf(b.status),
  )

  return (
    <Stack gap="xl">
      {/* KPI strip — 5 cards */}
      <Stack gap="sm">
        <SectionHead title="Ringkasan Keuangan" />
        <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="sm">
          <KpiCard
            value={formatIdr(lastMonth)}
            label="Bulan Ini"
            icon={<LuCircleDollarSign size={18} />}
            color="laut"
            small
            trend={momDelta}
          />
          <KpiCard
            value={formatIdr(paidTotal)}
            label="Total Terbayar"
            icon={<LuCircleCheck size={18} />}
            color="green"
            small
            sub={`${paid?.count ?? 0} invoice`}
          />
          <KpiCard
            value={formatIdr(outstanding)}
            label="Piutang Aktif"
            icon={<LuClock size={18} />}
            color="blue"
            small
            sub={`${outstandingCount} invoice`}
          />
          <KpiCard
            value={formatIdr(overdueTotal)}
            label="Tunggakan"
            icon={<LuTriangleAlert size={18} />}
            color={overdueTotal > 0 ? 'red' : 'gray'}
            small
            sub={`${overdue?.count ?? 0} invoice`}
          />
          <KpiCard
            value={`${collectionRate}%`}
            label="Collection Rate"
            icon={<LuCircleDollarSign size={18} />}
            color={collectionRate >= 80 ? 'teal' : collectionRate >= 50 ? 'yellow' : 'red'}
            small
            sub={collectionRate >= 80 ? 'Sehat' : collectionRate >= 50 ? 'Perlu perhatian' : 'Kritis'}
          />
        </SimpleGrid>
      </Stack>

      {/* Overdue alert */}
      {overdueTotal > 0 && (
        <Box
          px="md"
          py="sm"
          style={{
            borderRadius: 10,
            background: 'light-dark(var(--mantine-color-red-0), rgba(200,60,60,0.1))',
          }}
        >
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xs">
              <ThemeIcon variant="light" color="red" size="sm" radius="xl">
                <LuTriangleAlert size={13} />
              </ThemeIcon>
              <Text size="sm" fw={600} c="red.7">
                {overdue?.count ?? 0} invoice tunggakan — total {formatIdr(overdueTotal)}
              </Text>
            </Group>
            <Anchor component={Link} to="/dashboard/billing/invoices" c="red.6" size="xs" fw={600}>
              Lihat invoice →
            </Anchor>
          </Group>
        </Box>
      )}

      {/* Cashflow proportion bar */}
      {cashTotal > 0 && (
        <Stack gap="sm">
          <SectionHead title="Proporsi Arus Kas" />
          <Card radius="md" padding="lg">
            {/* Stacked proportion bar */}
            <Group gap={0} mb="md" style={{ borderRadius: 8, overflow: 'hidden', height: 28 }}>
              {[
                { value: paidTotal, color: 'var(--mantine-color-teal-5)', label: 'Terbayar' },
                { value: outstanding, color: 'var(--mantine-color-blue-5)', label: 'Piutang' },
                { value: overdueTotal, color: 'var(--mantine-color-red-5)', label: 'Tunggakan' },
                { value: otherTotal, color: 'var(--mantine-color-gray-3)', label: 'Lainnya' },
              ]
                .filter((s) => s.value > 0)
                .map((s, i, arr) => (
                  <Tooltip key={s.label} label={`${s.label}: ${formatIdr(s.value)}`} withArrow>
                    <div
                      style={{
                        flex: s.value / cashTotal,
                        background: s.color,
                        borderRadius: i === 0 ? '8px 0 0 8px' : i === arr.length - 1 ? '0 8px 8px 0' : '0',
                        minWidth: 4,
                      }}
                    />
                  </Tooltip>
                ))}
            </Group>

            {/* Legend */}
            <Group gap="lg" wrap="wrap">
              {[
                { label: 'Terbayar', value: paidTotal, color: 'teal' },
                { label: 'Piutang Aktif', value: outstanding, color: 'blue' },
                { label: 'Tunggakan', value: overdueTotal, color: 'red' },
                { label: 'Lainnya', value: otherTotal, color: 'gray' },
              ]
                .filter((s) => s.value > 0)
                .map((s) => (
                  <Group key={s.label} gap={6} wrap="nowrap">
                    <Box
                      w={10}
                      h={10}
                      style={{ borderRadius: 3, background: `var(--mantine-color-${s.color}-5)`, flexShrink: 0 }}
                    />
                    <Text size="xs" c="dimmed">
                      {s.label}
                    </Text>
                    <Text size="xs" fw={600}>
                      {((s.value / cashTotal) * 100).toFixed(0)}%
                    </Text>
                  </Group>
                ))}
            </Group>
          </Card>
        </Stack>
      )}

      {/* Revenue trend */}
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <SectionHead title="Tren Revenue" noMargin />
          <Group gap={4}>
            {(['3', '6', '12', '24'] as const).map((p) => (
              <UnstyledButton
                key={p}
                onClick={() => setPeriod(p)}
                style={{
                  padding: '4px 10px',
                  borderRadius: 999,
                  fontSize: '0.78rem',
                  fontWeight: period === p ? 600 : 500,
                  background: period === p ? 'var(--mantine-color-laut-6)' : 'transparent',
                  color: period === p ? 'white' : 'var(--mantine-color-dimmed)',
                  transition: 'background 120ms ease',
                }}
              >
                {p}B
              </UnstyledButton>
            ))}
          </Group>
        </Group>
        <Card radius="md" padding="lg">
          <Group justify="space-between" mb="md" wrap="wrap" gap="xs">
            <div>
              <Text size="xs" c="dimmed">
                Invoice PAID · {months} bulan terakhir
              </Text>
              {periodTotal > 0 && (
                <Text size="sm" fw={700} c="navy.8">
                  Total {formatIdr(periodTotal)}
                </Text>
              )}
            </div>
            {peakBucket && peakBucket.revenue > 0 && (
              <Group gap="xl" wrap="wrap">
                <div>
                  <Text size="xs" c="dimmed">
                    Bulan terbaik
                  </Text>
                  <Text size="sm" fw={600} c="teal.7">
                    {peakBucket.month} · {formatIdr(peakBucket.revenue)}
                  </Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Rata-rata/bulan
                  </Text>
                  <Text size="sm" fw={600}>
                    {formatIdr(avgMonthly)}
                  </Text>
                </div>
              </Group>
            )}
          </Group>
          {buckets.every((b) => b.revenue === 0) ? (
            <EmptyBlock label="Belum ada invoice terbayar di periode ini" />
          ) : (
            <Chart option={buildRevenueChart(buckets)} height={280} />
          )}
        </Card>
      </Stack>

      {/* Status mix — grouped, prioritised */}
      <Stack gap="sm">
        <SectionHead title="Rincian Status Invoice" />
        <Card radius="md" padding="lg">
          {sortedMix.length === 0 ? (
            <EmptyBlock label="Belum ada invoice" />
          ) : (
            <Stack gap="md">
              {sortedMix.map((s) => {
                const value = Number(s.total)
                const pct = cashTotal > 0 ? (value / cashTotal) * 100 : 0
                const c = statusColor(s.status)
                const isAttention = s.status === 'OVERDUE'
                return (
                  <div
                    key={s.status}
                    style={
                      isAttention
                        ? {
                            padding: '10px 12px',
                            borderRadius: 8,
                            background: 'light-dark(var(--mantine-color-red-0), rgba(200,60,60,0.08))',
                          }
                        : undefined
                    }
                  >
                    <Group justify="space-between" mb={6} wrap="nowrap">
                      <Group gap="xs" wrap="nowrap">
                        <KindBadge category={s.status} />
                        <Text size="xs" c="dimmed">
                          {s.count} invoice
                        </Text>
                      </Group>
                      <Group gap={10} wrap="nowrap">
                        <Text size="xs" c="dimmed">
                          {pct.toFixed(1)}%
                        </Text>
                        <CurrencyText idr={s.total} fw={700} size="sm" c={isAttention ? 'red.7' : undefined} />
                      </Group>
                    </Group>
                    <Progress value={pct} color={c} size={isAttention ? 'md' : 'sm'} radius="xl" />
                  </div>
                )
              })}
            </Stack>
          )}
        </Card>
      </Stack>
    </Stack>
  )
}

// ─── Shared UI helpers ────────────────────────────────

function SectionHead({
  title,
  badge,
  noMargin,
}: {
  title: string
  badge?: { count: number; color: string }
  noMargin?: boolean
}) {
  return (
    <Group gap="xs" align="center" mb={noMargin ? 0 : undefined}>
      <Text fw={700} size="md" ff="var(--font-display)" c="navy.8" style={{ letterSpacing: '-0.01em' }}>
        {title}
      </Text>
      {badge && badge.count > 0 && (
        <Badge size="sm" color={badge.color} variant="light">
          {badge.count}
        </Badge>
      )}
    </Group>
  )
}

function CardLabel({ children, noMargin }: { children: ReactNode; noMargin?: boolean }) {
  return (
    <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={noMargin ? 0 : 'md'} style={{ letterSpacing: '0.06em' }}>
      {children}
    </Text>
  )
}

function KpiCard({
  icon,
  value,
  label,
  color,
  sub,
  small,
  trend,
}: {
  icon: ReactNode
  value: ReactNode
  label: string
  color: string
  sub?: string
  small?: boolean
  trend?: number | null
}) {
  return (
    <Card radius="md" padding="md">
      <Group justify="space-between" align="flex-start" mb={4} wrap="nowrap">
        <Text size={small ? 'lg' : 'xl'} fw={700} lh={1.1} style={{ flex: 1, minWidth: 0 }}>
          {value}
        </Text>
        <ThemeIcon variant="light" color={color} size="md" radius="md" style={{ flexShrink: 0 }}>
          {icon}
        </ThemeIcon>
      </Group>
      <Text size="xs" c="dimmed" lh={1.3}>
        {label}
      </Text>
      {typeof trend === 'number' && trend !== 0 && (
        <Text size="xs" c={trend > 0 ? 'teal.7' : 'red.7'} fw={600} mt={4}>
          {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% vs bulan lalu
        </Text>
      )}
      {sub && (
        <Text size="xs" c={color} fw={500} mt={4} lh={1.3}>
          {sub}
        </Text>
      )}
    </Card>
  )
}

function SummaryChip({
  label,
  value,
  sub,
  color,
  icon,
  small,
}: {
  label: string
  value: ReactNode
  sub?: string
  color: string
  icon: ReactNode
  small?: boolean
}) {
  return (
    <Card radius="md" padding="sm">
      <Group gap="xs" mb={2} wrap="nowrap">
        <ThemeIcon variant="light" color={color} size="sm" radius="sm">
          {icon}
        </ThemeIcon>
        <Text size="xs" c="dimmed" truncate>
          {label}
        </Text>
      </Group>
      <Text fw={700} size={small ? 'md' : 'lg'} lh={1.1} truncate>
        {value}
      </Text>
      {sub && (
        <Text size="xs" c="dimmed" mt={2} lh={1.3} truncate>
          {sub}
        </Text>
      )}
    </Card>
  )
}

function EmptyBlock({ label }: { label: string }) {
  return (
    <Box py="xl" ta="center">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
    </Box>
  )
}

function SectionSkeleton({ kpis, blocks }: { kpis: number; blocks: number }) {
  return (
    <Stack gap="xl">
      <SimpleGrid cols={{ base: 2, sm: Math.min(kpis, 4) }} spacing="sm">
        {Array.from({ length: kpis }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-count skeleton
          <Skeleton key={i} height={88} radius="md" />
        ))}
      </SimpleGrid>
      {Array.from({ length: blocks }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed-count skeleton
        <Skeleton key={i} height={240} radius="md" />
      ))}
    </Stack>
  )
}

function statusColor(status: string): string {
  switch (status) {
    case 'PAID':
      return 'teal'
    case 'PARTIALLY_PAID':
      return 'cyan'
    case 'SENT':
      return 'blue'
    case 'OVERDUE':
      return 'red'
    case 'DRAFT':
    case 'PRO_FORMA':
      return 'gray'
    case 'CANCELLED':
      return 'dark'
    default:
      return 'gray'
  }
}

// ─── ECharts builders ─────────────────────────────────

function buildByTypeChart(data: { type: string; count: number }[]): any {
  const sorted = [...data].sort((a, b) => a.count - b.count)
  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: 90, right: 16, top: 8, bottom: 8 },
    xAxis: { type: 'value', show: false },
    yAxis: {
      type: 'category',
      data: sorted.map((d) => d.type.replace(/_/g, ' ')),
      axisLabel: { fontSize: 11 },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        type: 'bar',
        data: sorted.map((d) => d.count),
        itemStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 1,
            y2: 0,
            colorStops: [
              { offset: 0, color: '#1A9C98' },
              { offset: 1, color: '#3AAFAB' },
            ],
          },
          borderRadius: [0, 6, 6, 0],
        },
        label: { show: true, position: 'right', fontSize: 11, color: '#8FA0A4' },
        barMaxWidth: 22,
      },
    ],
    animationDuration: 700,
    animationEasing: 'cubicOut',
  }
}

function buildSizeDonut(dist: { small: number; medium: number; large: number; total: number }): any {
  return {
    tooltip: { trigger: 'item', formatter: '{b}<br/>{c} kapal ({d}%)' },
    legend: { orient: 'horizontal', bottom: 0, itemWidth: 10, itemHeight: 10, textStyle: { fontSize: 11 } },
    series: [
      {
        type: 'pie',
        radius: ['55%', '78%'],
        center: ['50%', '44%'],
        avoidLabelOverlap: false,
        label: {
          show: true,
          position: 'center',
          fontSize: 20,
          fontWeight: 700,
          fontFamily: 'Fraunces, Georgia, serif',
          formatter: `${dist.total}\n{sub|kapal}`,
          rich: { sub: { fontSize: 10, color: '#8FA0A4', padding: [4, 0, 0, 0] } },
        },
        itemStyle: { borderWidth: 3, borderColor: 'var(--mantine-color-body)' },
        data: [
          { value: dist.small, name: 'Kecil (<9 m)', itemStyle: { color: '#3AAFAB' } },
          { value: dist.medium, name: 'Sedang (9–15 m)', itemStyle: { color: '#7CB4D8' } },
          { value: dist.large, name: 'Besar (>15 m)', itemStyle: { color: '#E07856' } },
        ],
      },
    ],
    animationDuration: 700,
    animationEasing: 'cubicOut',
  }
}

function buildRevenueChart(buckets: { month: string; revenue: number }[]): any {
  const max = Math.max(...buckets.map((b) => b.revenue), 1)
  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line', lineStyle: { color: '#0E7C7B', opacity: 0.4 } },
      formatter: (params: any) => `<b>${params[0].name}</b><br/>${formatIdr(params[0].value)}`,
    },
    grid: { left: 64, right: 16, top: 16, bottom: 40 },
    xAxis: {
      type: 'category',
      data: buckets.map((b) => b.month),
      axisLabel: { rotate: 30, fontSize: 10 },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 10,
        formatter: (v: number) => (v >= 1_000_000 ? `${(v / 1_000_000).toFixed(0)}M` : `${(v / 1_000).toFixed(0)}K`),
      },
      splitLine: { lineStyle: { type: 'dashed', opacity: 0.35 } },
    },
    series: [
      {
        type: 'bar',
        data: buckets.map((b) => b.revenue),
        itemStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: '#1A9C98' },
              { offset: 1, color: '#0E7C7B' },
            ],
          },
          borderRadius: [6, 6, 0, 0],
        },
        emphasis: {
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#FB8B6A' },
                { offset: 1, color: '#E07856' },
              ],
            },
          },
        },
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: '#0E7C7B', opacity: 0.25, type: 'dashed' },
          data: [{ type: 'average', name: 'Rata-rata' }],
          label: { fontSize: 10, formatter: (p: any) => `Avg ${formatIdr(p.value)}` },
        },
        barMaxWidth: 40,
      },
    ],
    visualMap: {
      show: false,
      dimension: 1,
      min: 0,
      max,
      inRange: { colorAlpha: [0.65, 1] },
    },
    animationDuration: 800,
    animationEasing: 'cubicOut',
  }
}
